import { useApiUserApplicationsCommentsCreate, useApiUserApplicationsPartialUpdate } from "@/api/lctApIComponents";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useAppSelector } from "@/hooks/reduxHooks";
import { useToast } from "@/hooks/use-toast";
import { useUserAppInfo } from "@/hooks/useUserAppInfo";
import { ApplicationStatus, CommentType } from "@/utils/constants";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { UserApplicationComment } from "@/api/lctApISchemas";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { useFeedback } from "@/hooks/useFeedback";
import { AutoCarbonScore } from "@/components/AutoCarbonScore";

interface IProps {
  disabled?: boolean;
  userAppId: string;
  action: "approve" | "reject" | "requestAmendments";
  onActionComplete?: () => void;
}

const adminFormActionFormSchema = (carbonScoreRequired: boolean) =>
  z.object({
    carbonScore: z
      .number({
        message: "Carbon score must be a number",
      })
      .min(0, "Score must be at least 0")
      .max(99, "Score must be less than 100")
      .optional()
      .refine((val) => !carbonScoreRequired || val !== undefined, {
        message: "Carbon score is required",
      }),
    commentText: z.string().optional(),
  });

type AdminFormActionFormValues = z.infer<ReturnType<typeof adminFormActionFormSchema>>;

export const AdminFormAction: React.FC<IProps> = ({ disabled, userAppId, action, onActionComplete }) => {
  const updateApplication = useApiUserApplicationsPartialUpdate();
  const addComment = useApiUserApplicationsCommentsCreate();
  const token = useAppSelector((state) => state.user.authKey);
  const { refetchUserAppData, currentPart, userApplication } = useUserAppInfo(userAppId);
  const { refetchFeedbackItems } = useFeedback(userAppId);
  const { toast } = useToast();

  const showCarbonScore = currentPart === 2 && (action === "approve" || action === "reject");
  const carbonScoreRequired = showCarbonScore && action === "approve";

  const form = useForm<AdminFormActionFormValues>({
    resolver: zodResolver(adminFormActionFormSchema(carbonScoreRequired)),
    defaultValues: {
      carbonScore: userApplication?.carbon_score ? userApplication?.carbon_score : (userApplication?.proposed_carbon_score ?? undefined),
      commentText: "",
    },
    mode: "onChange",
  });

  const updateStatus = () => {
    switch (action) {
      case "approve":
        return {
          status: currentPart === 1 ? ApplicationStatus.PART_1_APPROVED : ApplicationStatus.APPROVED,
          submitTitle: currentPart === 1 ? "Approve Part 1" : "Approve",
          successTitle: currentPart === 1 ? "Part 1 Approved" : "Approved",
          commentType: showCarbonScore ? CommentType.CARBON_SCORE : CommentType.FEEDBACK,
          buttonType: "default" as const,
        };
      case "reject":
        return {
          status: ApplicationStatus.REJECTED,
          submitTitle: "Reject",
          successTitle: "Rejected",
          commentType: showCarbonScore ? CommentType.CARBON_SCORE : CommentType.FEEDBACK,
          buttonType: "destructive" as const,
        };
      case "requestAmendments":
        return {
          status: currentPart === 1 ? ApplicationStatus.FURTHER_INFO_PART_1 : ApplicationStatus.FURTHER_INFO_PART_2,
          submitTitle: "Request Amendments",
          successTitle: "Amendments requested",
          commentType: CommentType.FEEDBACK,
          buttonType: "outline" as const,
        };
    }
  };

  const onSubmit = async (values: AdminFormActionFormValues) => {
    if (!userAppId) return;
    try {
      await updateApplication.mutateAsync({
        headers: { Authorization: `Token ${token}` },
        pathParams: { id: userAppId },
        body: {
          status: updateStatus().status,
          carbon_score: values.carbonScore ?? undefined,
        },
      });

      if (values.commentText) {
        await addComment.mutateAsync({
          headers: { Authorization: `Token ${token}` },
          pathParams: { userapp: userAppId },
          body: {
            comment_text: values.commentText,
            comment_type: updateStatus().commentType,
          } as UserApplicationComment,
        });
      }

      await refetchUserAppData();
      await refetchFeedbackItems();
      form.reset();
      onActionComplete?.();
      toast({
        variant: "default",
        title: updateStatus().successTitle,
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error!",
        description: "Error approving application",
      });
    }
  };

  const onClose = () => {
    form.reset();
    onActionComplete?.();
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant={updateStatus().buttonType} disabled={disabled}>
          {updateStatus().submitTitle}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{updateStatus().submitTitle}</DialogTitle>
          <DialogDescription>
            <Form {...form}>
              <form id={action} onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                {showCarbonScore ? (
                  <>
                    <AutoCarbonScore userAppId={userAppId} />
                    <FormField
                      control={form.control}
                      name="carbonScore"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            Carbon Score {carbonScoreRequired ? <span className="text-md ml-1 font-medium text-error">*</span> : null}
                          </FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              type="number"
                              min={1}
                              max={99}
                              placeholder="1-99"
                              onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                              className="w-full"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </>
                ) : null}
                <FormField
                  control={form.control}
                  name="commentText"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Feedback</FormLabel>
                      <FormControl>
                        <Textarea {...field} placeholder="Type your feedback here..." className="mt-4 w-full" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </form>
            </Form>
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="gap-2">
          <DialogClose asChild>
            <Button type="button" variant="outline" onClick={() => onClose()}>
              Cancel
            </Button>
          </DialogClose>
          <DialogClose asChild>
            <Button
              type="submit"
              form={action}
              variant={action === "reject" ? "destructive" : "default"}
              disabled={currentPart === 2 && action === "approve" ? !form.formState.isValid : false}
            >
              {updateStatus().submitTitle}
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
